import { layoutSizes } from '@/theme/layoutSizes';
import { s, type SpacingToken } from '@/theme/tokens';
import { layoutModeTokens, useLayoutMode } from '@/ui/layouts/layoutMode';
import { Box } from './Box';
import { Container } from './Container';
import type { SxProps, Theme } from './types';
import type { ReactElement, ReactNode } from 'react';

const SPACING_MAP: Record<SpacingToken, string> = {
  none: s('none'),
  xxxs: s('xxxs'),
  xxs: s('xxs'),
  xs: s('xs'),
  sm: s('sm'),
  md: s('md'),
  lg: s('lg'),
  xl: s('xl'),
  '2xl': s('2xl'),
  '3xl': s('3xl'),
  '4xl': s('4xl'),
  '5xl': s('5xl'),
  gutter: s('gutter'),
  section: s('section'),
};

const resolveSpacing = (token: SpacingToken | undefined): string | number | undefined =>
  token ? SPACING_MAP[token] : undefined;

const capNonWideHorizontalSpacing = (token: SpacingToken): SpacingToken => {
  switch (token) {
    case 'none':
    case 'xxxs':
    case 'xxs':
    case 'xs':
    case 'sm':
      return token;
    case 'md':
      return 'sm';
    case 'lg':
      return 'md';
    case 'xl':
      return 'lg';
    case '2xl':
      return 'xl';
    case '3xl':
      return '2xl';
    case '4xl':
      return '3xl';
    case '5xl':
      return '4xl';
    case 'gutter':
    case 'section':
      return 'md';
    default:
      return token;
  }
};

const capWideHorizontalSpacing = (token: SpacingToken): boolean =>
  token === 'lg' ||
  token === 'xl' ||
  token === '2xl' ||
  token === '3xl' ||
  token === '4xl' ||
  token === '5xl' ||
  token === 'gutter' ||
  token === 'section';

const resolveTopSpacing = (
  paddingTop: SpacingToken | undefined,
  paddingY: SpacingToken | undefined,
): string | number | undefined => {
  if (paddingTop) return resolveSpacing(paddingTop);
  if (!paddingY) return undefined;
  if (paddingY === 'none') return SPACING_MAP.none;
  return s('md');
};

type PageShellProps = {
  children: ReactNode;
  maxWidth?: false | 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  paddingX?: SpacingToken;
  paddingY?: SpacingToken;
  paddingTop?: SpacingToken;
  paddingBottom?: SpacingToken;
  sx?: SxProps<Theme>;
};

export const PageShell = ({
  children,
  maxWidth = false,
  paddingX = 'none',
  paddingY = 'none',
  paddingTop,
  paddingBottom = 'md',
  sx,
}: PageShellProps): ReactElement => {
  const layoutMode = useLayoutMode();
  const clampXlWidth = maxWidth === 'xl';
  const resolvedMaxWidth = clampXlWidth ? false : maxWidth;
  const compactPaddingX = capNonWideHorizontalSpacing(paddingX);
  const capWidePaddingX = clampXlWidth && capWideHorizontalSpacing(paddingX);
  const resolvedPaddingX =
    layoutMode === layoutModeTokens.wide ? (capWidePaddingX ? 'xl' : paddingX) : compactPaddingX;
  const boostedPaddingX: SpacingToken = resolvedPaddingX === 'lg' ? 'xl' : resolvedPaddingX;
  const finalPaddingX: SpacingToken =
    layoutMode === layoutModeTokens.medium && boostedPaddingX === 'xl' ? 'lg' : boostedPaddingX;
  const resolvedPaddingTop = paddingTop;
  const resolvedPaddingY = paddingY;
  const resolvedPaddingBottom = paddingBottom;
  const contentMinHeight = paddingTop === 'xl' ? 1080 : undefined;

  return (
    <Container
      maxWidth={resolvedMaxWidth}
      disableGutters
      sx={[
        {
          px: resolveSpacing(finalPaddingX),
          pt: resolveTopSpacing(resolvedPaddingTop, resolvedPaddingY),
          pb: resolveSpacing(resolvedPaddingBottom),
          maxWidth: clampXlWidth ? layoutSizes.content.maxWidth : undefined,
          width: '100%',
          mx: clampXlWidth ? 'auto' : undefined,
          minWidth: 0,
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      <Box sx={{ minWidth: 0, minHeight: contentMinHeight }}>{children}</Box>
    </Container>
  );
};
